/**
 * Holds the auth state (the JWT returned by the Laravel native-auth endpoints):
 *   - keeps access + refresh tokens in localStorage (shared across tabs, so a
 *     link opened in a new tab stays authenticated),
 *   - decodes the JWT to expose display claims (name, preferred_username, IdBu),
 *   - silently refreshes ~60s before expiry,
 *   - a 401 interceptor refreshes-and-retries; if refresh fails -> logout.
 */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';

import type {AuthTokens} from './nativeAuthApi';
import {nativeRefresh} from './nativeAuthApi';
import {decodeJwt, isJwtExpired, JwtClaims} from './decodeJwt';

const ACCESS_KEY = 'native_auth_token';
const REFRESH_KEY = 'native_auth_refresh';
const REFRESH_SKEW_MS = 60_000;

/** Read a value from whichever storage currently holds the session. */
const readStored = (key: string): string | null =>
  localStorage.getItem(key) ?? sessionStorage.getItem(key);

/** Remove the native-auth tokens from both storages. */
const purgeNativeTokens = (): void => {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  sessionStorage.removeItem(ACCESS_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
};

interface NativeAuthContextValue {
  token: string | null;
  claims: JwtClaims | null;
  isAuthenticated: boolean;
  login: (tokens: AuthTokens) => void;
  logout: () => void;
}

const NativeAuthContext = createContext<NativeAuthContextValue>({
  token: null,
  claims: null,
  isAuthenticated: false,
  login: () => undefined,
  logout: () => undefined,
});

export const useNativeAuth = (): NativeAuthContextValue => useContext(NativeAuthContext);

interface NativeAuthProviderProps {
  children: ReactNode;
}

export const NativeAuthProvider = ({children}: NativeAuthProviderProps): JSX.Element => {
  const [token, setToken] = useState<string | null>(() => readStored(ACCESS_KEY));

  // Attach the bearer synchronously, during render, so the first data query on
  // mount/reload already carries it. The effects below (refresh scheduling, 401
  // interceptor) run after commit — too late for queries that fire on mount,
  // which would otherwise get a 403 from validateJWT before self-healing on
  // retry. Skip an expired token: the load effect refreshes that case, and a
  // stale bearer 403s without triggering the 401-only interceptor.
  if (token && !isJwtExpired(token)) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshingRef = useRef<Promise<string> | null>(null);
  const doRefreshRef = useRef<() => Promise<string>>(() => Promise.reject(new Error('not ready')));

  const claims = useMemo(() => (token ? decodeJwt(token) : null), [token]);

  const clearTimer = (): void => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const logout = useCallback((): void => {
    clearTimer();
    purgeNativeTokens();
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
  }, []);

  const scheduleRefresh = useCallback((access: string): void => {
    clearTimer();
    const decoded = decodeJwt(access);
    if (!decoded?.exp) {
      return;
    }
    const delay = Math.max(decoded.exp * 1000 - Date.now() - REFRESH_SKEW_MS, 0);
    timerRef.current = setTimeout(() => {
      void doRefreshRef.current();
    }, delay);
  }, []);

  const applyTokens = useCallback(
    (tokens: AuthTokens): void => {
      localStorage.setItem(ACCESS_KEY, tokens.accessToken);
      if (tokens.refreshToken) {
        localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${tokens.accessToken}`;
      setToken(tokens.accessToken);
      scheduleRefresh(tokens.accessToken);
    },
    [scheduleRefresh]
  );

  // Called by the login form once sign-in completes.
  const login = useCallback(
    (tokens: AuthTokens): void => {
      applyTokens(tokens);
    },
    [applyTokens]
  );

  const doRefresh = useCallback((): Promise<string> => {
    if (refreshingRef.current) {
      return refreshingRef.current;
    }
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (!refreshToken) {
      logout();
      return Promise.reject(new Error('No refresh token'));
    }
    const promise = (async () => {
      try {
        const result = await nativeRefresh(refreshToken);
        applyTokens(result);
        return result.accessToken;
      } catch (e) {
        logout();
        throw e;
      } finally {
        refreshingRef.current = null;
      }
    })();
    refreshingRef.current = promise;
    return promise;
  }, [applyTokens, logout]);

  useEffect(() => {
    doRefreshRef.current = doRefresh;
  }, [doRefresh]);

  // On load: schedule refresh for a live token, or refresh immediately if expired.
  useEffect(() => {
    const access = readStored(ACCESS_KEY);
    if (!access) {
      return;
    }
    if (isJwtExpired(access)) {
      void doRefresh();
    } else {
      axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;
      scheduleRefresh(access);
    }
    return clearTimer;
  }, [doRefresh, scheduleRefresh]);

  // Refresh-and-retry on 401; give up (logout) if the refresh itself fails.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config as (typeof error.config & {_retry?: boolean}) | undefined;
        const status = error.response?.status;
        const url: string = original?.url ?? '';
        if (status === 401 && original && !original._retry && !url.includes('native-auth/')) {
          original._retry = true;
          try {
            const access = await doRefresh();
            original.headers = {...original.headers, Authorization: `Bearer ${access}`};
            return axios(original);
          } catch {
            return Promise.reject(error);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [doRefresh]);

  const value = useMemo<NativeAuthContextValue>(
    () => ({
      token,
      claims,
      isAuthenticated: !!token,
      login,
      logout,
    }),
    [token, claims, login, logout]
  );

  return <NativeAuthContext.Provider value={value}>{children}</NativeAuthContext.Provider>;
};
